import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { ZitaLogo, ZitaWordmark } from '@/components/ZitaLogo';
import {
  Activity, Users, CreditCard, LifeBuoy, AlertTriangle,
  Server, BarChart3, Globe, LogOut, Sparkles, ShieldCheck,
} from 'lucide-react';

const NAV = [
  { to: '/admin', end: true, icon: Activity, label: 'Business Health', testId: 'admin-nav-live' },
  { to: '/admin/users', icon: Users, label: 'Users', testId: 'admin-nav-users' },
  { to: '/admin/subscriptions', icon: CreditCard, label: 'Subscriptions', testId: 'admin-nav-subs' },
  { to: '/admin/tickets', icon: LifeBuoy, label: 'Support', testId: 'admin-nav-tickets', showUnread: true },
  { to: '/admin/third-party-apps', icon: ShieldCheck, label: 'App Approvals', testId: 'admin-nav-third-party', showAppPending: true },
  { to: '/admin/analytics', icon: BarChart3, label: 'Analytics', testId: 'admin-nav-analytics' },
  { to: '/admin/marketplaces', icon: Globe, label: 'Marketplaces', testId: 'admin-nav-marketplaces' },
  { to: '/admin/errors', icon: AlertTriangle, label: 'Errors', testId: 'admin-nav-errors' },
  { to: '/admin/system', icon: Server, label: 'System', testId: 'admin-nav-system' },
];

/**
 * Admin Shell — visible ONLY to the Super Admin AFTER PIN step-up
 * (user.is_admin_active === true). A Super Admin who did NOT step up (plain
 * /login flow) sees `is_admin_active=false` and is bounced to /admin/login
 * to enter the PIN. Signing out drops the elevated token → next /auth/me
 * returns `is_admin_active=false` → this shell immediately re-gates.
 * Regular users hitting /admin/* are bounced to /login.
 */
export const AdminShell = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [pendingApps, setPendingApps] = useState(0);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate('/admin/login', { replace: true });
    else if (!user.is_admin_active) navigate('/admin/login', { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user?.is_admin_active) return;
    let cancelled = false;
    const pull = () => {
      api.get('/admin/tickets/unread_count')
        .then((r) => { if (!cancelled) setUnread(r.data.unread_tickets || 0); })
        .catch(() => {});
      api.get('/admin/third-party-apps?status=pending&limit=1')
        .then((r) => {
          if (!cancelled) setPendingApps(r.data.counts_by_status?.pending || 0);
        })
        .catch(() => {});
    };
    pull();
    const id = setInterval(pull, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  if (!user?.is_admin_active) return null;

  const doLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-zita-cream flex" data-testid="admin-shell">
      <aside className="hidden sm:flex flex-col w-64 border-r border-zita-taupe/60 bg-white/40 backdrop-blur">
        <div className="flex items-center gap-3 px-5 py-6 border-b border-zita-taupe/60">
          <ZitaLogo size={40} />
          <div className="flex flex-col leading-none">
            <ZitaWordmark className="text-2xl" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-zita-mocha mt-1">
              Super Admin
            </span>
          </div>
        </div>
        <nav className="flex flex-col p-3 gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                  isActive
                    ? 'bg-zita-sand text-zita-espresso font-semibold'
                    : 'text-zita-mocha hover:bg-zita-sand/60'
                }`
              }
            >
              <n.icon size={16} />
              <span className="flex-1">{n.label}</span>
              {n.showUnread && unread > 0 && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500 text-white font-semibold"
                  data-testid="admin-sidebar-unread-badge"
                >
                  {unread}
                </span>
              )}
              {n.showAppPending && pendingApps > 0 && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500 text-white font-semibold"
                  data-testid="admin-sidebar-app-pending-badge"
                >
                  {pendingApps}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto p-3 space-y-1 border-t border-zita-taupe/60">
          <div className="text-[11px] uppercase tracking-[0.16em] text-zita-mocha px-3 pb-2">
            {user.email}
          </div>
          <button
            type="button"
            onClick={() => navigate('/')}
            data-testid="admin-back-to-app"
            className="flex items-center gap-2 px-3 py-2 w-full rounded-lg text-sm text-zita-mocha hover:bg-zita-sand/60"
          >
            <Sparkles size={16} /> Back to Zita
          </button>
          <button
            type="button"
            onClick={doLogout}
            data-testid="admin-logout"
            className="flex items-center gap-2 px-3 py-2 w-full rounded-lg text-sm text-zita-mocha hover:bg-zita-sand/60"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminShell;
